/**
 * Security Validation Utility
 * Comprehensive security checks to prevent malicious file upload bypass
 */

export class SecurityValidator {
  static ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'pdf', 'xls', 'xlsx', 'txt', 'csv'];
  
  static ALLOWED_MIME_TYPES = [
    'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp',
    'application/pdf',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain', 'text/csv'
  ];
  
  static DANGEROUS_PATTERNS = [
    /\.php/i, /\.asp/i, /\.jsp/i, /\.js$/i, /\.html?$/i, /\.exe$/i, /\.bat$/i,
    /\.cmd$/i, /\.sh$/i, /\.py$/i, /\.rb$/i, /\.pl$/i, /\u0000/, /%00/, /\.\./ // NOSONAR
  ];

  /**
   * Validate file against all security criteria
   * @param {File} file - File to validate
   * @returns {Object} - Validation result
   */
  static validateFile(file) {
    const result = {
      isValid: true,
      errors: [],
      securityLevel: 'HIGH'
    };

    if (!file || !(file instanceof File)) {
      result.errors.push('Invalid file object');
      result.isValid = false;
      return result;
    }

    // 1. Extension validation
    const extension = file.name.toLowerCase().split('.').pop();
    if (!this.ALLOWED_EXTENSIONS.includes(extension)) {
      result.errors.push(`SECURITY VIOLATION: Extension .${extension} is forbidden`);
      result.isValid = false;
    }

    // 2. MIME type validation  
    if (!this.ALLOWED_MIME_TYPES.includes(file.type)) {
      result.errors.push(`SECURITY VIOLATION: MIME type ${file.type} is forbidden`);
      result.isValid = false;
    }

    // 3. Dangerous pattern detection
    for (const pattern of this.DANGEROUS_PATTERNS) {
      if (pattern.test(file.name)) {
        result.errors.push('SECURITY VIOLATION: Filename contains dangerous patterns');
        result.isValid = false;
        break;
      }
    }

    // 4. File size validation
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      result.errors.push(`File too large: ${(file.size / 1024 / 1024).toFixed(1)}MB (max: 5MB)`);
      result.isValid = false;
    }

    if (file.size === 0) {
      result.errors.push('Empty file detected');
      result.isValid = false;
    }

    return result;
  }

  /**
   * Validate payload contains only secure attachments
   * @param {Object} payload - Request payload
   * @returns {Object} - Validation result
   */
  static validatePayload(payload) {
    const result = {
      isValid: true,
      errors: [],
      attachmentCount: 0
    };

    if (!payload || !payload.Attachments) {
      return result; // No attachments to validate
    }

    result.attachmentCount = payload.Attachments.length;

    payload.Attachments.forEach((attachment, index) => {
      // Check for security validation markers
      if (!attachment.IsSecureValidated) {
        result.errors.push(`Attachment ${index + 1} missing security validation`);
        result.isValid = false;
      }

      // Validate attachment properties
      if (!attachment.AttachmentName || !attachment.AttachmentType) {
        result.errors.push(`Attachment ${index + 1} missing required properties`);
        result.isValid = false;
      }

      // Check for dangerous patterns in attachment metadata
      for (const pattern of this.DANGEROUS_PATTERNS) {
        if (pattern.test(attachment.AttachmentName)) {
          result.errors.push(`Attachment ${index + 1} name contains dangerous patterns`);
          result.isValid = false;
          break;
        }
      }
    });

    return result;
  }

  /**
   * Log security validation event
   * @param {string} event - Event type
   * @param {Object} details - Event details
   */
  static logSecurityEvent(event, details) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      event: event,
      details: details,
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    console.log('[SECURITY VALIDATOR]', logEntry);

    // Send to security monitoring if endpoint available
    if (window.securityMonitoring) {
      window.securityMonitoring.logEvent(logEntry);
    }
  }

  /**
   * Get security configuration status
   * @returns {Object} - Current security status
   */
  static getSecurityStatus() {
    return {
      fileUploadSecurity: !!window.fileUploadSecurity,
      httpMethodSecurity: !!window.httpMethodSecurity,
      browserSecurity: !!window.browserSecurityService,
      slowlorisProtection: !!window.slowlorisProtection,
      allowedExtensions: this.ALLOWED_EXTENSIONS,
      allowedMimeTypes: this.ALLOWED_MIME_TYPES,
      securityLevel: 'MAXIMUM'
    };
  }
}

export default SecurityValidator;